/**
 * DoctorSchedule.jsx
 * src/pages/assistant/DoctorSchedule.jsx
 *
 * Features:
 *  - Weekly grid view per doctor
 *  - Filter by doctor & day
 *  - Add / Edit / Delete schedule slots (DB connected)
 *  - Toggle Active / Inactive status
 *  - Slot duration (time between appointments) setting
 *  - Computed total daily slots shown
 *  - Doctor stats summary cards
 */

import { useState, useEffect, useCallback } from "react";
import { BSModal, PageHeader, Loading, EmptyState, Avatar, apiFetch } from "../../components/helpers";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT = { Monday:"Mon", Tuesday:"Tue", Wednesday:"Wed", Thursday:"Thu", Friday:"Fri", Saturday:"Sat", Sunday:"Sun" };
const DURATIONS = [15, 20, 30, 45, 60];

const EMPTY_FORM = {
  schedule_id: null,
  doctor_id: "",
  day_of_week: "Monday",
  start_time: "09:00",
  end_time: "17:00",
  slot_duration: 30,
  status: "Active",
};

function computeSlots(start, end, duration) {
  try {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    const totalMin = (eh * 60 + em) - (sh * 60 + sm);
    return totalMin > 0 ? Math.floor(totalMin / duration) : 0;
  } catch { return 0; }
}

function StatusPill({ status, onChange }) {
  const active = status === "Active";
  return (
    <button
      onClick={onChange}
      style={{
        fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
        border: "none", cursor: "pointer",
        background: active ? "#E1F5EE" : "#F0F4F8",
        color: active ? "#085041" : "#6B7A8D",
        transition: "all .2s"
      }}
    >
      {active ? "● Active" : "○ Off"}
    </button>
  );
}

function DoctorCard({ doctor, schedules }) {
  const activeDays = schedules.filter(s => s.doctor_id == doctor.doctor_id && s.status === "Active").length;
  const totalSlots = schedules
    .filter(s => s.doctor_id == doctor.doctor_id && s.status === "Active")
    .reduce((acc, s) => acc + computeSlots(s.start_time, s.end_time, s.slot_duration || 30), 0);

  return (
    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14, borderLeft: "3px solid #D85A30" }}>
      <div className="card-body p-3 d-flex align-items-center gap-3">
        <Avatar name={doctor.name} size={44} />
        <div style={{ flex: 1 }}>
          <div className="fw-bold" style={{ fontSize: 13, color: "#0A1628" }}>{doctor.name}</div>
          <div className="text-muted" style={{ fontSize: 11 }}>{doctor.specialization}</div>
          <div className="mt-1 d-flex gap-2">
            <span style={{ fontSize: 10, background: "#E1F5EE", color: "#085041", borderRadius: 20, padding: "1px 8px", fontWeight: 700 }}>
              {activeDays} days active
            </span>
            <span style={{ fontSize: 10, background: "#FAEEDA", color: "#633806", borderRadius: 20, padding: "1px 8px", fontWeight: 700 }}>
              {totalSlots} slots/week
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DoctorSchedule() {
  const [doctors,     setDoctors]     = useState([]);
  const [schedules,   setSchedules]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selectedDoc, setSelectedDoc] = useState("All");
  const [selectedDay, setSelectedDay] = useState("All");
  const [viewMode,    setViewMode]    = useState("grid"); // "grid" | "list"
  const [modalOpen,   setModalOpen]   = useState(false);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");
  const [flash,       setFlash]       = useState({ msg: "", type: "success" });
  const [toggling,    setToggling]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [schedRes, docRes] = await Promise.all([
        apiFetch("get_all_schedule.php"),
        apiFetch("get_all_doctors.php"),
      ]);
      setSchedules(schedRes.data || []);
      setDoctors(docRes.data || []);
    } catch {
      setSchedules([]); setDoctors([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showFlash = (msg, type = "success") => {
    setFlash({ msg, type });
    setTimeout(() => setFlash({ msg: "", type: "success" }), 3000);
  };

  const openAdd = () => {
    setForm({ ...EMPTY_FORM });
    setError("");
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setForm({
      schedule_id: s.schedule_id,
      doctor_id: s.doctor_id,
      day_of_week: s.day_of_week,
      start_time: s.start_time?.slice(0, 5) || "09:00",
      end_time: s.end_time?.slice(0, 5) || "17:00",
      slot_duration: s.slot_duration || 30,
      status: s.status || "Active",
    });
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.doctor_id)  return setError("Please select a doctor");
    if (!form.day_of_week) return setError("Day is required");
    if (!form.start_time)  return setError("Start time is required");
    if (!form.end_time)    return setError("End time is required");
    if (form.start_time >= form.end_time) return setError("End time must be after start time");

    setSubmitting(true); setError("");
    const isEdit = !!form.schedule_id;
    const endpoint = isEdit ? "update_doctor_schedule.php" : "add_doctor_schedule.php";
    try {
      const res = await apiFetch(endpoint, { method: "POST", body: form });
      if (res.status) {
        setModalOpen(false);
        showFlash(isEdit ? "Schedule updated!" : "Schedule slot added!");
        load();
      } else {
        setError(res.message || "Something went wrong");
      }
    } catch { setError("Network error"); }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this schedule slot?")) return;
    try {
      const res = await apiFetch("delete_schedule.php", { method: "POST", body: { schedule_id: id } });
      if (res.status) { showFlash("Slot removed!", "danger"); load(); }
    } catch {}
  };

  const toggleStatus = async (s) => {
    setToggling(s.schedule_id);
    const newStatus = s.status === "Active" ? "Inactive" : "Active";
    try {
      const res = await apiFetch("change_schedule_status.php", {
        method: "POST",
        body: { schedule_id: s.schedule_id, status: newStatus }
      });
      if (res.status) {
        setSchedules(prev => prev.map(x => x.schedule_id === s.schedule_id ? { ...x, status: newStatus } : x));
      }
    } catch {}
    setToggling(null);
  };

  // Filtered schedules
  const filtered = schedules.filter(s =>
    (selectedDoc === "All" || String(s.doctor_id) === String(selectedDoc)) &&
    (selectedDay === "All" || s.day_of_week === selectedDay)
  );

  // Unique doctors with schedules for stats
  const scheduledDoctorIds = [...new Set(schedules.map(s => s.doctor_id))];
  const scheduledDoctors = doctors.filter(d => scheduledDoctorIds.includes(String(d.doctor_id)) || scheduledDoctorIds.includes(d.doctor_id));

  const inp = "form-control";
  const slots = computeSlots(form.start_time, form.end_time, form.slot_duration || 30);

  return (
    <div className="p-3 p-lg-4" style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}>

      <PageHeader title="Doctor Schedules" subtitle="Manage weekly availability & appointment slots">
        {/* Doctor filter */}
        <select className="form-select" value={selectedDoc} onChange={e => setSelectedDoc(e.target.value)} style={{ width: 160, fontSize: 13 }}>
          <option value="All">All Doctors</option>
          {doctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{d.name}</option>)}
        </select>
        {/* Day filter */}
        <select className="form-select" value={selectedDay} onChange={e => setSelectedDay(e.target.value)} style={{ width: 130, fontSize: 13 }}>
          <option value="All">All Days</option>
          {DAYS.map(d => <option key={d}>{d}</option>)}
        </select>
        {/* View toggle */}
        <div className="btn-group">
          <button className={`btn btn-sm fw-bold ${viewMode === "grid" ? "btn-dark" : "btn-outline-secondary"}`} style={{ fontSize: 13 }} onClick={() => setViewMode("grid")}>⊞ Grid</button>
          <button className={`btn btn-sm fw-bold ${viewMode === "list" ? "btn-dark" : "btn-outline-secondary"}`} style={{ fontSize: 13 }} onClick={() => setViewMode("list")}>☰ List</button>
        </div>
        <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30", borderRadius: 10 }} onClick={openAdd}>
          + Add Slot
        </button>
      </PageHeader>

      {/* Flash */}
      {flash.msg && (
        <div className={`alert alert-${flash.type === "danger" ? "warning" : "success"} py-2 px-3 mb-3`} style={{ fontSize: 13 }}>
          {flash.msg}
        </div>
      )}

      {loading ? <Loading /> : (
        <>
          {/* Doctor stats cards */}
          {scheduledDoctors.length > 0 && (
            <div className="row g-3 mb-4">
              {scheduledDoctors.map(doc => (
                <div key={doc.doctor_id} className="col-12 col-md-6 col-xl-4">
                  <DoctorCard doctor={doc} schedules={schedules} />
                </div>
              ))}
            </div>
          )}

          {/* ── GRID VIEW ── */}
          {viewMode === "grid" && (
            <div className="row g-3">
              {DAYS.filter(day => selectedDay === "All" || day === selectedDay).map(day => {
                const daySlots = filtered.filter(s => s.day_of_week === day);
                return (
                  <div className={`col-12 ${selectedDay === "All" ? "col-md-6 col-xl-3" : ""}`} key={day}>
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
                      <div className="card-header border-0 py-2 px-3 d-flex align-items-center justify-content-between"
                        style={{ background: "#F8FAFC", fontSize: 13, fontWeight: 700, color: "#0A1628", borderRadius: "14px 14px 0 0" }}>
                        <span>{day}</span>
                        <span className="badge text-bg-secondary" style={{ fontSize: 10 }}>{daySlots.length} slot{daySlots.length !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="card-body p-2">
                        {daySlots.length === 0 ? (
                          <p className="text-muted text-center py-3 mb-0" style={{ fontSize: 12 }}>No slots scheduled</p>
                        ) : (
                          daySlots.map((s) => {
                            const slotsCount = computeSlots(s.start_time, s.end_time, s.slot_duration || 30);
                            const isActive = s.status === "Active";
                            return (
                              <div key={s.schedule_id} className="mb-2 p-2 rounded-2"
                                style={{ background: isActive ? "#EAFAF4" : "#F8FAFC", border: `1px solid ${isActive ? "#B2E8D5" : "#E8ECF0"}`, opacity: isActive ? 1 : 0.7 }}>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div style={{ flex: 1 }}>
                                    <div className="fw-semibold d-flex align-items-center gap-1" style={{ fontSize: 12, color: "#0A1628" }}>
                                      <Avatar name={s.doctor_name || "?"} size={20} />
                                      <span>{s.doctor_name || "Unknown"}</span>
                                    </div>
                                    <div style={{ fontSize: 11, color: "#6B7A8D", fontFamily: "monospace", marginTop: 2 }}>
                                      🕐 {s.start_time?.slice(0,5)} – {s.end_time?.slice(0,5)}
                                    </div>
                                    <div style={{ fontSize: 10, color: "#6B7A8D" }}>
                                      ⏱ {s.slot_duration}min/slot · 📋 {slotsCount} slots
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column align-items-end gap-1">
                                    <StatusPill
                                      status={toggling === s.schedule_id ? (isActive ? "Inactive" : "Active") : s.status}
                                      onChange={() => toggleStatus(s)}
                                    />
                                    <div className="d-flex gap-1 mt-1">
                                      <button className="btn btn-sm py-0 px-1" title="Edit"
                                        style={{ fontSize: 12, background: "#EEF2F7" }} onClick={() => openEdit(s)}>✏️</button>
                                      <button className="btn btn-sm py-0 px-1" title="Delete"
                                        style={{ fontSize: 12, background: "#FDE8E8" }} onClick={() => handleDelete(s.schedule_id)}>🗑</button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── LIST VIEW ── */}
          {viewMode === "list" && (
            <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <div className="card-body p-0">
                {filtered.length === 0 ? <EmptyState message="No schedule slots found." /> : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead style={{ background: "#FAFBFC" }}>
                        <tr>
                          {["Doctor", "Day", "Time", "Slot Duration", "Slots/Day", "Status", "Actions"].map(h => (
                            <th key={h} className="text-muted fw-bold border-0 px-4 py-3"
                              style={{ fontSize: 10, letterSpacing: ".6px", textTransform: "uppercase" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(s => {
                          const slotsCount = computeSlots(s.start_time, s.end_time, s.slot_duration || 30);
                          return (
                            <tr key={s.schedule_id}>
                              <td className="px-4">
                                <div className="d-flex align-items-center gap-2">
                                  <Avatar name={s.doctor_name || "?"} size={28} />
                                  <div>
                                    <div className="fw-semibold" style={{ fontSize: 13 }}>{s.doctor_name || "—"}</div>
                                    <div className="text-muted" style={{ fontSize: 11 }}>{s.specialization || ""}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 fw-semibold" style={{ fontSize: 13 }}>{DAY_SHORT[s.day_of_week] || s.day_of_week}</td>
                              <td className="px-4 text-muted" style={{ fontSize: 13, fontFamily: "monospace" }}>
                                {s.start_time?.slice(0,5)} – {s.end_time?.slice(0,5)}
                              </td>
                              <td className="px-4 text-muted" style={{ fontSize: 12 }}>{s.slot_duration} min</td>
                              <td className="px-4">
                                <span className="badge text-bg-primary" style={{ fontSize: 11 }}>{slotsCount} slots</span>
                              </td>
                              <td className="px-4">
                                <StatusPill status={s.status} onChange={() => toggleStatus(s)} />
                              </td>
                              <td className="px-4">
                                <div className="d-flex gap-1">
                                  <button className="btn btn-sm fw-bold" style={{ background: "#FAECE7", color: "#D85A30", fontSize: 11 }} onClick={() => openEdit(s)}>Edit</button>
                                  <button className="btn btn-sm btn-outline-danger fw-bold" style={{ fontSize: 11 }} onClick={() => handleDelete(s.schedule_id)}>Delete</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {filtered.length === 0 && viewMode === "grid" && <EmptyState message="No schedule slots found. Click '+ Add Slot' to get started." />}
        </>
      )}

      {/* ── ADD / EDIT MODAL ── */}
      <BSModal open={modalOpen} title={form.schedule_id ? "Edit Schedule Slot" : "Add Schedule Slot"} size="modal-lg" onClose={() => setModalOpen(false)}>
        <div className="row g-3">
          {/* Doctor */}
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>DOCTOR *</label>
            <select className="form-select" value={form.doctor_id} onChange={e => setForm({ ...form, doctor_id: e.target.value })} disabled={!!form.schedule_id}>
              <option value="">Select Doctor</option>
              {doctors.map(d => <option key={d.doctor_id} value={d.doctor_id}>{d.name} — {d.specialization}</option>)}
            </select>
          </div>
          {/* Day */}
          <div className="col-md-6">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>DAY OF WEEK *</label>
            <select className="form-select" value={form.day_of_week} onChange={e => setForm({ ...form, day_of_week: e.target.value })} disabled={!!form.schedule_id}>
              {DAYS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
          {/* Start time */}
          <div className="col-md-4">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>START TIME *</label>
            <input type="time" className={inp} value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} />
          </div>
          {/* End time */}
          <div className="col-md-4">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>END TIME *</label>
            <input type="time" className={inp} value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} />
          </div>
          {/* Slot duration */}
          <div className="col-md-4">
            <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>SLOT DURATION (min)</label>
            <select className="form-select" value={form.slot_duration} onChange={e => setForm({ ...form, slot_duration: Number(e.target.value) })}>
              {DURATIONS.map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          {/* Status (only on edit) */}
          {form.schedule_id && (
            <div className="col-md-6">
              <label className="form-label fw-bold text-muted" style={{ fontSize: 11 }}>STATUS</label>
              <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          )}
          {/* Live preview */}
          {form.start_time && form.end_time && (
            <div className="col-12">
              <div className="alert alert-info py-2 px-3 mb-0 d-flex align-items-center gap-2" style={{ fontSize: 12 }}>
                <span>📋</span>
                <span>
                  This creates <strong>{slots}</strong> appointment slot{slots !== 1 ? "s" : ""} of{" "}
                  <strong>{form.slot_duration} min</strong> each between{" "}
                  <strong>{form.start_time}</strong> – <strong>{form.end_time}</strong>
                </span>
              </div>
            </div>
          )}
        </div>
        {error && <div className="alert alert-danger py-2 px-3 mt-3 mb-0" style={{ fontSize: 12 }}>{error}</div>}
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button className="btn btn-light fw-semibold" onClick={() => setModalOpen(false)}>Cancel</button>
          <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30" }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</> : (form.schedule_id ? "Update Slot" : "Add Slot")}
          </button>
        </div>
      </BSModal>
    </div>
  );
}